/**
 * What a social post can carry: one image or one MP4 video.
 * Keep the limits in sync with worker/src/routes/adminSocial.ts.
 */

import type { SocialPlatform } from "@/types";

export const MEDIA_ACCEPT = "image/png,image/jpeg,image/gif,image/webp,video/mp4";
const MEDIA_TYPES = new Set(MEDIA_ACCEPT.split(","));

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
// Bluesky rejects image blobs over 1 MB (videos go through its video service instead)
export const BLUESKY_MAX_IMAGE_BYTES = 1_000_000;

export const isVideoType = (contentType: string | null | undefined) => Boolean(contentType?.startsWith("video/"));

export const formatMb = (bytes: number) => `${(bytes / 1_000_000).toFixed(1)} MB`;

/** Why this file can't be attached to a post going to `platforms`, or null when it can. */
export function mediaProblem(file: { type: string; size: number }, platforms: SocialPlatform[] = []): string | null {
  if (!MEDIA_TYPES.has(file.type)) return "must be a PNG, JPEG, GIF or WebP image, or an MP4 video";
  if (isVideoType(file.type)) {
    return file.size > MAX_VIDEO_BYTES ? `is ${formatMb(file.size)}; videos can be up to 50 MB` : null;
  }
  if (file.size > MAX_IMAGE_BYTES) return `is ${formatMb(file.size)}; images can be up to 5 MB`;
  if (file.size > BLUESKY_MAX_IMAGE_BYTES && platforms.includes("bluesky")) {
    return `is ${formatMb(file.size)}; Bluesky allows images up to 1 MB`;
  }
  return null;
}
